export function createDrawingOverlay({
    playerRootEl,
    videoElement = null,
    requestFullscreenOnOpen = true,
}) {
    if (!playerRootEl) {
        return null;
    }

    const config = {
        defaultColor: '#ff0000',
        defaultLineWidth: 3,
        nonDrawBottomOffsetPx: 100,
        eraserThresholdPx: 20,
    };

    let drawingMode = false;
    let isDrawing = false;
    let currentTool = 'pencil';
    let currentColor = config.defaultColor;
    let currentLineWidth = config.defaultLineWidth;
    let startX = 0;
    let startY = 0;
    let drawingElements = [];
    let currentStroke = [];
    let hadFullscreenDuringSession = false;

    const elements = {
        toggleButton: null,
        toolbar: null,
        canvas: null,
        tempCanvas: null,
        statusText: null,
    };

    const contexts = {
        canvas: null,
        tempCanvas: null,
    };

    const listeners = [];
    let saveStatusTimer = null;

    function on(target, eventName, handler, options) {
        target.addEventListener(eventName, handler, options);
        listeners.push(() => target.removeEventListener(eventName, handler, options));
    }

    function getControlBarHeight() {
        const controlBar = playerRootEl.querySelector('.vjs-control-bar');
        if (!controlBar) return config.nonDrawBottomOffsetPx;
        return Math.max(controlBar.offsetHeight || 0, 40);
    }

    function isFullscreenActive() {
        return !!(
            document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.mozFullScreenElement ||
            document.msFullscreenElement ||
            (playerRootEl.classList && playerRootEl.classList.contains('vjs-fullscreen'))
        );
    }

    function requestPlayerFullscreen() {
        if (!requestFullscreenOnOpen || isFullscreenActive()) return;

        const requestFullscreen =
            playerRootEl.requestFullscreen ||
            playerRootEl.webkitRequestFullscreen ||
            playerRootEl.mozRequestFullScreen ||
            playerRootEl.msRequestFullscreen;

        if (typeof requestFullscreen === 'function') {
            const res = requestFullscreen.call(playerRootEl);
            if (res && typeof res.catch === 'function') {
                res.catch(() => { });
            }
        }
    }

    function updateCanvasSize() {
        if (!elements.canvas || !elements.tempCanvas) return;

        const drawHeight = Math.max(0, playerRootEl.clientHeight - getControlBarHeight());
        elements.canvas.width = playerRootEl.clientWidth;
        elements.canvas.height = drawHeight;
        elements.tempCanvas.width = playerRootEl.clientWidth;
        elements.tempCanvas.height = drawHeight;

        if (drawingMode) {
            redrawCanvas();
        }
    }

    function redrawCanvas() {
        if (!contexts.canvas || !elements.canvas) return;
        contexts.canvas.clearRect(0, 0, elements.canvas.width, elements.canvas.height);

        drawingElements.forEach(element => {
            contexts.canvas.strokeStyle = element.color;
            contexts.canvas.lineWidth = element.lineWidth;
            contexts.canvas.lineCap = 'round';
            contexts.canvas.lineJoin = 'round';
            contexts.canvas.globalCompositeOperation = 'source-over';

            if (element.type === 'line') {
                contexts.canvas.beginPath();
                contexts.canvas.moveTo(element.startX, element.startY);
                contexts.canvas.lineTo(element.endX, element.endY);
                contexts.canvas.stroke();
            } else if (element.type === 'circle') {
                contexts.canvas.beginPath();
                contexts.canvas.arc(element.centerX, element.centerY, element.radius, 0, Math.PI * 2);
                contexts.canvas.stroke();
            } else if (element.type === 'pencil' && element.points.length) {
                contexts.canvas.beginPath();
                contexts.canvas.moveTo(element.points[0].x, element.points[0].y);
                for (let i = 1; i < element.points.length; i += 1) {
                    contexts.canvas.lineTo(element.points[i].x, element.points[i].y);
                }
                contexts.canvas.stroke();
            }
        });
    }

    function clearDrawings() {
        drawingElements = [];
        currentStroke = [];
        redrawCanvas();
    }

    function slugifyPart(value, fallback = 'video') {
        const s = String(value || '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
        return s || fallback;
    }

    function getVideoNamePart() {
        if (!videoElement) return 'video';
        const src = videoElement.currentSrc || videoElement.src || '';
        if (!src) return 'video';
        try {
            const url = new URL(src, window.location.href);
            const fileName = (url.pathname.split('/').pop() || '').split('?')[0];
            const baseName = fileName.replace(/\.[a-z0-9]+$/i, '');
            return slugifyPart(baseName, 'video');
        } catch (err) {
            const raw = src.split('/').pop() || '';
            return slugifyPart(raw.replace(/\.[a-z0-9]+$/i, ''), 'video');
        }
    }

    function getSaveFileName() {
        const d = new Date();
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const dateStr = months[d.getMonth()] + d.getDate() + '-' + String(d.getHours()).padStart(2, '0') + String(d.getMinutes()).padStart(2, '0');
        const videoName = getVideoNamePart();
        const timeSec = Math.max(0, Math.floor(videoElement?.currentTime || 0));
        return `rowerup-${videoName}-t${timeSec}s-${dateStr}.png`;
    }

    function downloadBlob(blob, fileName) {
        const a = document.createElement('a');
        const href = URL.createObjectURL(blob);
        a.href = href;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(href);
    }

    async function saveBlobWithPicker(blob, fileName) {
        if (typeof window.showSaveFilePicker !== 'function') {
            return false;
        }

        try {
            const handle = await window.showSaveFilePicker({
                suggestedName: fileName,
                types: [{
                    description: 'PNG Image',
                    accept: { 'image/png': ['.png'] },
                }],
            });
            const writable = await handle.createWritable();
            await writable.write(blob);
            await writable.close();
            return true;
        } catch (err) {
            // User cancellation or unsupported browser path: use default download flow.
            return false;
        }
    }

    function saveAsPng() {
        if (!elements.canvas) return;
        showSaveStatus('Saving...');

        const exportCanvas = document.createElement('canvas');
        exportCanvas.width = elements.canvas.width;
        exportCanvas.height = elements.canvas.height;
        const exportCtx = exportCanvas.getContext('2d');
        if (!exportCtx) {
            showSaveStatus('Save failed', true);
            return;
        }

        try {
            if (videoElement && videoElement.readyState >= 2) {
                exportCtx.drawImage(videoElement, 0, 0, exportCanvas.width, exportCanvas.height);
            }
        } catch (err) {
            // Keep export working even if video frame cannot be read.
            console.warn('Drawing export: unable to read video frame, saving overlay only.', err);
        }

        exportCtx.drawImage(elements.canvas, 0, 0);
        if (elements.tempCanvas) {
            exportCtx.drawImage(elements.tempCanvas, 0, 0);
        }

        exportCanvas.toBlob(async blob => {
            if (!blob) {
                showSaveStatus('Save failed', true);
                return;
            }
            const fileName = getSaveFileName();
            const savedByPicker = await saveBlobWithPicker(blob, fileName);
            if (!savedByPicker) {
                downloadBlob(blob, fileName);
                showSaveStatus('Download started');
                return;
            }
            showSaveStatus('Saved');
        }, 'image/png');
    }

    function showSaveStatus(text, isError = false) {
        if (!elements.statusText) return;
        if (saveStatusTimer) {
            clearTimeout(saveStatusTimer);
            saveStatusTimer = null;
        }
        elements.statusText.textContent = text;
        elements.statusText.style.display = 'inline-block';
        elements.statusText.style.background = isError ? 'rgba(217, 83, 79, 0.9)' : 'rgba(47, 140, 255, 0.9)';
        saveStatusTimer = setTimeout(() => {
            if (elements.statusText) {
                elements.statusText.style.display = 'none';
            }
            saveStatusTimer = null;
        }, 2200);
    }

    function getCoords(evt) {
        const rect = elements.canvas.getBoundingClientRect();
        const scaleX = elements.canvas.width / rect.width;
        const scaleY = elements.canvas.height / rect.height;
        return {
            x: (evt.clientX - rect.left) * scaleX,
            y: (evt.clientY - rect.top) * scaleY,
        };
    }

    function drawTempLine(ctx, x1, y1, x2, y2) {
        ctx.strokeStyle = currentColor;
        ctx.lineWidth = currentLineWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.globalCompositeOperation = 'source-over';
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
    }

    function drawTempCircle(ctx, centerX, centerY, radius) {
        ctx.strokeStyle = currentColor;
        ctx.lineWidth = currentLineWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.globalCompositeOperation = 'source-over';
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.stroke();
    }

    function distanceBetweenPoints(x1, y1, x2, y2) {
        const dx = x1 - x2;
        const dy = y1 - y2;
        return Math.sqrt((dx * dx) + (dy * dy));
    }

    function distanceToLineSegment(px, py, x1, y1, x2, y2) {
        const A = px - x1;
        const B = py - y1;
        const C = x2 - x1;
        const D = y2 - y1;

        const dot = A * C + B * D;
        const lenSq = C * C + D * D;
        let param = -1;
        if (lenSq !== 0) {
            param = dot / lenSq;
        }

        let xx;
        let yy;
        if (param < 0) {
            xx = x1;
            yy = y1;
        } else if (param > 1) {
            xx = x2;
            yy = y2;
        } else {
            xx = x1 + (param * C);
            yy = y1 + (param * D);
        }

        const dx = px - xx;
        const dy = py - yy;
        return Math.sqrt((dx * dx) + (dy * dy));
    }

    function findElementNearCoordinates(x, y, threshold = config.eraserThresholdPx) {
        for (let i = drawingElements.length - 1; i >= 0; i -= 1) {
            const element = drawingElements[i];

            if (element.type === 'line') {
                const dist = distanceToLineSegment(x, y, element.startX, element.startY, element.endX, element.endY);
                if (dist <= threshold) {
                    return i;
                }
            } else if (element.type === 'circle') {
                const distanceToCenter = distanceBetweenPoints(x, y, element.centerX, element.centerY);
                const distanceToCircumference = Math.abs(distanceToCenter - element.radius);
                if (distanceToCircumference <= threshold) {
                    return i;
                }
            } else if (element.type === 'pencil' && element.points && element.points.length) {
                for (let j = 0; j < element.points.length; j += 1) {
                    const pt = element.points[j];
                    const dx = x - pt.x;
                    const dy = y - pt.y;
                    const dist = Math.sqrt((dx * dx) + (dy * dy));
                    if (dist <= threshold) {
                        return i;
                    }
                }
            }
        }
        return -1;
    }

    function startDrawing(evt) {
        if (!drawingMode) return;
        const c = getCoords(evt);
        startX = c.x;
        startY = c.y;

        if (currentTool === 'pencil') {
            isDrawing = true;
            currentStroke = [{ x: c.x, y: c.y }];
        } else if (currentTool === 'line') {
            isDrawing = true;
        } else if (currentTool === 'circle') {
            isDrawing = true;
        } else if (currentTool === 'eraser') {
            const idx = findElementNearCoordinates(c.x, c.y);
            if (idx !== -1) {
                drawingElements.splice(idx, 1);
                redrawCanvas();
            }
        }
    }

    function draw(evt) {
        if (!drawingMode || !isDrawing) return;
        const c = getCoords(evt);

        if (currentTool === 'pencil') {
            currentStroke.push({ x: c.x, y: c.y });
            contexts.tempCanvas.clearRect(0, 0, elements.tempCanvas.width, elements.tempCanvas.height);
            contexts.tempCanvas.strokeStyle = currentColor;
            contexts.tempCanvas.lineWidth = currentLineWidth;
            contexts.tempCanvas.lineCap = 'round';
            contexts.tempCanvas.lineJoin = 'round';
            contexts.tempCanvas.globalCompositeOperation = 'source-over';
            contexts.tempCanvas.beginPath();
            contexts.tempCanvas.moveTo(currentStroke[0].x, currentStroke[0].y);
            for (let i = 1; i < currentStroke.length; i += 1) {
                contexts.tempCanvas.lineTo(currentStroke[i].x, currentStroke[i].y);
            }
            contexts.tempCanvas.stroke();
        } else if (currentTool === 'line') {
            contexts.tempCanvas.clearRect(0, 0, elements.tempCanvas.width, elements.tempCanvas.height);
            drawTempLine(contexts.tempCanvas, startX, startY, c.x, c.y);
        } else if (currentTool === 'circle') {
            const radius = distanceBetweenPoints(startX, startY, c.x, c.y);
            contexts.tempCanvas.clearRect(0, 0, elements.tempCanvas.width, elements.tempCanvas.height);
            drawTempCircle(contexts.tempCanvas, startX, startY, radius);
        }
    }

    function stopDrawing(evt) {
        if (!drawingMode || !isDrawing) return;
        const c = getCoords(evt);

        if (currentTool === 'pencil' && currentStroke.length > 0) {
            drawingElements.push({
                type: 'pencil',
                points: [...currentStroke],
                color: currentColor,
                lineWidth: currentLineWidth,
            });
            currentStroke = [];
            redrawCanvas();
        } else if (currentTool === 'line') {
            drawingElements.push({
                type: 'line',
                startX,
                startY,
                endX: c.x,
                endY: c.y,
                color: currentColor,
                lineWidth: currentLineWidth,
            });
            redrawCanvas();
        } else if (currentTool === 'circle') {
            drawingElements.push({
                type: 'circle',
                centerX: startX,
                centerY: startY,
                radius: distanceBetweenPoints(startX, startY, c.x, c.y),
                color: currentColor,
                lineWidth: currentLineWidth,
            });
            redrawCanvas();
        }

        contexts.tempCanvas.clearRect(0, 0, elements.tempCanvas.width, elements.tempCanvas.height);
        isDrawing = false;
    }

    function open() {
        if (drawingMode) {
            close();
            return;
        }
        drawingMode = true;
        hadFullscreenDuringSession = false;

        elements.toolbar.style.display = 'flex';
        elements.canvas.style.display = 'block';
        elements.canvas.style.pointerEvents = 'auto';
        elements.tempCanvas.style.display = 'block';
        redrawCanvas();

        if (videoElement && videoElement.paused) {
            videoElement.play().catch(() => { });
        }
        requestPlayerFullscreen();
        if (isFullscreenActive()) {
            hadFullscreenDuringSession = true;
        }
    }

    function close() {
        if (!drawingMode) return;
        drawingMode = false;
        isDrawing = false;
        hadFullscreenDuringSession = false;
        elements.toolbar.style.display = 'none';
        elements.canvas.style.display = 'none';
        elements.canvas.style.pointerEvents = 'none';
        elements.tempCanvas.style.display = 'none';
    }

    function destroy() {
        close();
        listeners.splice(0).forEach(dispose => dispose());
        if (elements.toolbar) elements.toolbar.remove();
        if (elements.toggleButton) elements.toggleButton.remove();
        if (elements.tempCanvas) elements.tempCanvas.remove();
        if (elements.canvas) elements.canvas.remove();
    }

    function buildUI() {
        playerRootEl.style.position = 'relative';

        const canvas = document.createElement('canvas');
        canvas.style.position = 'absolute';
        canvas.style.left = '0';
        canvas.style.top = '0';
        canvas.style.width = '100%';
        canvas.style.pointerEvents = 'none';
        canvas.style.touchAction = 'none';
        canvas.style.zIndex = '9999';
        canvas.style.display = 'none';

        const tempCanvas = document.createElement('canvas');
        tempCanvas.style.position = 'absolute';
        tempCanvas.style.left = '0';
        tempCanvas.style.top = '0';
        tempCanvas.style.width = '100%';
        tempCanvas.style.pointerEvents = 'none';
        tempCanvas.style.touchAction = 'none';
        tempCanvas.style.zIndex = '9998';
        tempCanvas.style.display = 'none';

        const toggleButton = document.createElement('button');
        toggleButton.type = 'button';
        toggleButton.textContent = 'Draw';
        toggleButton.style.position = 'absolute';
        toggleButton.style.top = '12px';
        toggleButton.style.right = '12px';
        toggleButton.style.zIndex = '10001';
        toggleButton.style.border = 'none';
        toggleButton.style.borderRadius = '16px';
        toggleButton.style.padding = '7px 12px';
        toggleButton.style.background = '#2f8cff';
        toggleButton.style.color = '#fff';
        toggleButton.style.fontSize = '12px';
        toggleButton.style.fontWeight = '600';
        toggleButton.style.cursor = 'pointer';

        const toolbar = document.createElement('div');
        toolbar.style.position = 'absolute';
        toolbar.style.top = '12px';
        toolbar.style.left = '50%';
        toolbar.style.transform = 'translateX(-50%)';
        toolbar.style.zIndex = '10000';
        toolbar.style.display = 'none';
        toolbar.style.gap = '8px';
        toolbar.style.alignItems = 'center';
        toolbar.style.padding = '8px 10px';
        toolbar.style.borderRadius = '16px';
        toolbar.style.background = 'rgba(0,0,0,0.45)';
        toolbar.style.backdropFilter = 'blur(6px)';

        function makeBtn(label, onClick, bg = '#4b5b66') {
            const b = document.createElement('button');
            b.type = 'button';
            b.textContent = label;
            b.style.border = 'none';
            b.style.borderRadius = '8px';
            b.style.padding = '6px 9px';
            b.style.background = bg;
            b.style.color = '#fff';
            b.style.fontSize = '12px';
            b.style.cursor = 'pointer';
            on(b, 'click', onClick);
            return b;
        }

        let pencilBtn;
        let lineBtn;
        let circleBtn;
        let eraserBtn;

        function setTool(tool) {
            currentTool = tool;
            if (pencilBtn) pencilBtn.style.background = tool === 'pencil' ? '#2f8cff' : '#4b5b66';
            if (lineBtn) lineBtn.style.background = tool === 'line' ? '#2f8cff' : '#4b5b66';
            if (circleBtn) circleBtn.style.background = tool === 'circle' ? '#2f8cff' : '#4b5b66';
            if (eraserBtn) eraserBtn.style.background = tool === 'eraser' ? '#f0ad4e' : '#4b5b66';
        }

        pencilBtn = makeBtn('Pencil', () => { setTool('pencil'); }, '#2f8cff');
        lineBtn = makeBtn('Line', () => { setTool('line'); });
        circleBtn = makeBtn('Circle', () => { setTool('circle'); });
        eraserBtn = makeBtn('Eraser', () => { setTool('eraser'); });
        const clearBtn = makeBtn('Clear', clearDrawings);
        const saveBtn = makeBtn('Save PNG', saveAsPng, '#2f8cff');
        const closeBtn = makeBtn('Close', close, '#d9534f');
        const statusText = document.createElement('span');
        statusText.style.display = 'none';
        statusText.style.padding = '6px 8px';
        statusText.style.borderRadius = '8px';
        statusText.style.color = '#fff';
        statusText.style.fontSize = '12px';
        statusText.style.fontWeight = '600';

        const colorInput = document.createElement('input');
        colorInput.type = 'color';
        colorInput.value = currentColor;
        colorInput.style.width = '30px';
        colorInput.style.height = '28px';
        colorInput.style.padding = '0';
        colorInput.style.border = 'none';
        colorInput.style.background = 'transparent';
        on(colorInput, 'input', (evt) => { currentColor = evt.target.value; });

        const widthInput = document.createElement('input');
        widthInput.type = 'range';
        widthInput.min = '1';
        widthInput.max = '10';
        widthInput.value = String(currentLineWidth);
        widthInput.style.width = '85px';
        widthInput.style.height = '6px';
        widthInput.style.webkitAppearance = 'none';
        widthInput.style.appearance = 'none';
        widthInput.style.background = 'rgba(255,255,255,0.35)';
        widthInput.style.borderRadius = '3px';
        widthInput.style.outline = 'none';

        // Safari needs explicit thumb styling via a stylesheet rule.
        const sliderStyle = document.createElement('style');
        sliderStyle.textContent = `
            input[type=range]::-webkit-slider-thumb {
                -webkit-appearance: none;
                appearance: none;
                width: 14px;
                height: 14px;
                border-radius: 50%;
                background: #ffffff;
                cursor: pointer;
            }
        `;
        toolbar.appendChild(sliderStyle);

        on(widthInput, 'input', (evt) => { currentLineWidth = parseInt(evt.target.value, 10); });

        toolbar.appendChild(pencilBtn);
        toolbar.appendChild(lineBtn);
        toolbar.appendChild(circleBtn);
        toolbar.appendChild(eraserBtn);
        toolbar.appendChild(colorInput);
        toolbar.appendChild(widthInput);
        toolbar.appendChild(saveBtn);
        toolbar.appendChild(clearBtn);
        toolbar.appendChild(closeBtn);
        toolbar.appendChild(statusText);

        on(toggleButton, 'click', open);
        on(canvas, 'mousedown', startDrawing);
        on(canvas, 'mousemove', draw);
        on(canvas, 'mouseup', stopDrawing);
        on(canvas, 'mouseleave', stopDrawing);

        on(canvas, 'touchstart', evt => {
            evt.preventDefault();
            const t = evt.touches[0];
            startDrawing({ clientX: t.clientX, clientY: t.clientY });
        }, { passive: false });
        on(canvas, 'touchmove', evt => {
            evt.preventDefault();
            const t = evt.touches[0];
            draw({ clientX: t.clientX, clientY: t.clientY });
        }, { passive: false });
        on(canvas, 'touchend', evt => {
            evt.preventDefault();
            stopDrawing(evt.changedTouches[0] || { clientX: 0, clientY: 0 });
        }, { passive: false });

        const handleFullscreenChange = () => {
            updateCanvasSize();
            if (!drawingMode) return;
            if (isFullscreenActive()) {
                hadFullscreenDuringSession = true;
                return;
            }
            if (hadFullscreenDuringSession) {
                close();
            }
        };

        on(document, 'fullscreenchange', handleFullscreenChange);
        on(document, 'webkitfullscreenchange', handleFullscreenChange);
        on(document, 'mozfullscreenchange', handleFullscreenChange);
        on(document, 'MSFullscreenChange', handleFullscreenChange);
        on(window, 'resize', updateCanvasSize);

        elements.canvas = canvas;
        elements.tempCanvas = tempCanvas;
        elements.toggleButton = toggleButton;
        elements.toolbar = toolbar;
        elements.statusText = statusText;
        contexts.canvas = canvas.getContext('2d');
        contexts.tempCanvas = tempCanvas.getContext('2d');

        playerRootEl.appendChild(tempCanvas);
        playerRootEl.appendChild(canvas);
        playerRootEl.appendChild(toolbar);
        playerRootEl.appendChild(toggleButton);

        updateCanvasSize();
    }

    buildUI();

    return { open, close, destroy };
}
